import ClientsPurchaseBehavior from './components/biReports/ClientsPurchaseBehavior';
import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import BackofficeReportsApi from './components/biReports/backofficeReportsApi';
import ClientsActivationSummary from './components/biReports/ClientsActivationSummary';
import ClientsAccountValidation from './components/biReports/ClientsAccountValidation';
import ClientsActivationBreakdown from './components/biReports/ClientsActivationBreakdown';
import ClientsActivationChart from './components/biReports/ClientsActivationChart';
import ClientsCohorts from './components/biReports/ClientsCohorts';
import ClientsPurchaseProgression, { purchaseProgressionCsvRows } from './components/biReports/ClientsPurchaseProgression';
import { csvCell, reportErrorMessage } from './components/biReports/clientPurchaseReportUtils';
import { reportActionSx, reportBodySx, reportHeaderSx, reportPeriodControlSx, reportTitleSx } from './components/biReports/clientReportStyles';
import ClientReportDetails from './components/biReports/ClientReportDetails';
import CustomPeriodPopover, { businessTodayIso, validateCustomPeriod } from './components/biReports/CustomPeriodPopover';

const numberFormatter = new Intl.NumberFormat('ro-RO', {
  maximumFractionDigits: 0,
});

function formatPeriodRange(period) {
  if (!period?.dateFrom || !period?.dateTo) return '';
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  const from = new Date(`${period.dateFrom}T12:00:00`).toLocaleDateString('ro-RO', options);
  const to = new Date(`${period.dateTo}T12:00:00`).toLocaleDateString('ro-RO', options);
  return `${from} – ${to}`;
}

function formatUpdateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString('ro-RO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Bucharest',
  });
}

export function accountValidationCsvRows(summary = {}) {
  const validation = summary.account_validation || {};
  return [
    ['Account validation', 'Clients', 'Share of registrations %'].map(csvCell).join(','),
    ['Validated', validation.validated?.value, validation.validated?.share].map(csvCell).join(','),
    ['Not validated', validation.not_validated?.value, validation.not_validated?.share].map(csvCell).join(','),
    ['Suspended', validation.suspended?.value, validation.suspended?.share].map(csvCell).join(','),
  ];
}

class RapClientsActivation extends React.Component {
  constructor(props) {
    super(props);
    this.isMountedFlag = false;
    this.initialLoadTimer = null;
    this.request = null;
    this.requestId = 0;
    this.cache = {};
    this.cacheUpdatedAt = {};
    this.customPeriodButton = React.createRef();
    this.state = {
      periodMode: 'last_90d',
      dateFrom: '',
      dateTo: '',
      customPeriodOpen: false,
      draftDateFrom: '',
      draftDateTo: '',
      customPeriodError: '',
      countyFilter: 'all',
      windowDays: 7,
      breakdownDimension: 'county',
      countyOptions: [{ value: 'all', label: 'All counties' }],
      response: null,
      status: 'idle',
      updatedAt: null,
      errorMessage: null,
    };
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.initialLoadTimer = window.setTimeout(() => {
      this.initialLoadTimer = null;
      if (this.isMountedFlag) this.loadActivation();
    }, 0);
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    if (this.initialLoadTimer !== null) {
      window.clearTimeout(this.initialLoadTimer);
      this.initialLoadTimer = null;
    }
    this.requestId += 1;
    if (this.request && typeof this.request.abort === 'function') {
      this.request.abort();
    }
    this.request = null;
  }

  cacheKey = (
    periodMode = this.state.periodMode,
    county = this.state.countyFilter,
    windowDays = this.state.windowDays,
  ) => `${periodMode}:${county}:${windowDays}${periodMode === 'custom' ? `:${this.state.dateFrom}:${this.state.dateTo}` : ''}`;

  loadActivation = ({ force = false } = {}) => {
    const { periodMode, countyFilter, windowDays } = this.state;
    if (periodMode === 'custom' && (!this.state.dateFrom || !this.state.dateTo || this.state.dateFrom > this.state.dateTo)) return;
    const cacheKey = this.cacheKey(periodMode, countyFilter, windowDays);
    if (!force && this.cache[cacheKey]) {
      this.requestId += 1;
      if (this.request && typeof this.request.abort === 'function') {
        this.request.abort();
      }
      this.request = null;
      this.setState({
        response: this.cache[cacheKey],
        status: 'success',
        updatedAt: this.cacheUpdatedAt[cacheKey] || null,
      });
      return;
    }

    const requestId = this.requestId + 1;
    this.requestId = requestId;
    if (this.request && typeof this.request.abort === 'function') {
      this.request.abort();
    }

    const request = BackofficeReportsApi.getClientsActivationAsync(
      this.props.client,
      {
        periodMode,
        windowDays,
        county: countyFilter,
        granularity: 'auto',
        ...(periodMode === 'custom' ? { dateFrom: this.state.dateFrom, dateTo: this.state.dateTo } : {}),
      },
    );
    this.request = request;
    this.setState({ status: 'loading', errorMessage: null });

    const handleSuccess = (data) => {
      if (!this.isMountedFlag || this.requestId !== requestId) return;
      this.request = null;
      const updatedAt = data?.generated_at || new Date().toISOString();
      this.cache[cacheKey] = data;
      this.cacheUpdatedAt[cacheKey] = updatedAt;
      this.setState({
        response: data || null,
        countyOptions: Array.isArray(data?.county_options) && data.county_options.length
          ? data.county_options
          : this.state.countyOptions,
        status: 'success',
        updatedAt,
      });
    };
    const handleFailure = (xhr, textStatus) => {
      if (textStatus === 'abort') return;
      if (!this.isMountedFlag || this.requestId !== requestId) return;
      this.request = null;
      this.setState({ status: 'error', errorMessage: reportErrorMessage(xhr) });
    };

    if (request && typeof request.done === 'function') {
      request.done(handleSuccess);
      request.fail(handleFailure);
    } else {
      Promise.resolve(request).then(handleSuccess).catch(handleFailure);
    }
  };

  selectCachedResponse = (periodMode, countyFilter, windowDays) => ({
    response: this.cache[this.cacheKey(periodMode, countyFilter, windowDays)] || null,
    updatedAt: this.cacheUpdatedAt[
      this.cacheKey(periodMode, countyFilter, windowDays)
    ] || null,
  });

  handlePeriodChange = (event, periodMode) => {
    if (periodMode === 'custom') {
      this.setState({
        customPeriodOpen: true,
        draftDateFrom: this.state.dateFrom || this.state.response?.period?.dateFrom || businessTodayIso(),
        draftDateTo: this.state.dateTo || this.state.response?.period?.dateTo || businessTodayIso(),
        customPeriodError: '',
      });
      return;
    }
    if (!periodMode || periodMode === this.state.periodMode) return;
    const cached = this.selectCachedResponse(
      periodMode,
      this.state.countyFilter,
      this.state.windowDays,
    );
    this.setState({ periodMode, ...cached }, () => this.loadActivation());
  };

  handleCountyChange = (event, option) => {
    const countyFilter = option?.value || 'all';
    if (countyFilter === this.state.countyFilter) return;
    const cached = this.selectCachedResponse(
      this.state.periodMode,
      countyFilter,
      this.state.windowDays,
    );
    this.setState({ countyFilter, ...cached }, () => this.loadActivation());
  };

  handleWindowChange = (event, windowDays) => {
    if (!windowDays || windowDays === this.state.windowDays) return;
    const cached = this.selectCachedResponse(
      this.state.periodMode,
      this.state.countyFilter,
      windowDays,
    );
    this.setState({ windowDays, ...cached }, () => this.loadActivation());
  };

  handleDimensionChange = (event, breakdownDimension) => {
    if (breakdownDimension) this.setState({ breakdownDimension });
  };

  handleCustomDateChange = (field, value) => {
    this.setState({ [field === 'dateFrom' ? 'draftDateFrom' : 'draftDateTo']: value, customPeriodError: '' });
  };

  handleCustomPeriodApply = () => {
    const { draftDateFrom, draftDateTo } = this.state;
    const customPeriodError = validateCustomPeriod(draftDateFrom, draftDateTo);
    if (customPeriodError) {
      this.setState({ customPeriodError });
      return;
    }
    this.setState({
      periodMode: 'custom',
      dateFrom: draftDateFrom,
      dateTo: draftDateTo,
      customPeriodOpen: false,
      response: null,
      updatedAt: null,
    }, () => this.loadActivation());
  };

  exportUnvalidatedCreditClients = async () => {
    try {
      await BackofficeReportsApi.downloadUnvalidatedCreditClients(this.props.client);
    } catch (error) {
      window.alert(error.message || 'Download failed. Please try again.');
    }
  };

  exportCsv = () => {
    const { response, breakdownDimension } = this.state;
    if (!response) return;
    const period = response.period || {};
    const filters = response.filters || {};
    const county = filters.county || {};
    const summary = response.summary || {};
    const curve = response.activation_curve || {};
    const breakdown = response.breakdowns?.[breakdownDimension] || [];
    const dataQuality = response.data_quality || {};
    const lines = [
      ['Client activation and repeat purchases'].map(csvCell).join(','),
      ['Registration cohort', period.label].map(csvCell).join(','),
      ['Date from', period.dateFrom].map(csvCell).join(','),
      ['Date to', period.dateTo].map(csvCell).join(','),
      ['Observation time', response.as_of].map(csvCell).join(','),
      ['Timezone', period.timezone].map(csvCell).join(','),
      ['Client profile county', county.label].map(csvCell).join(','),
      ['Selected activation window', `${filters.windowDays} days`].map(csvCell).join(','),
      ['Window definition', 'Exact elapsed 24-hour days from registration to first qualifying purchase'].map(csvCell).join(','),
      ['Qualifying order status codes', '1, 2, 5'].map(csvCell).join(','),
      ['Purchase exclusions', 'Negative-value orders and financial reversal documents; genuine zero-value purchases count'].map(csvCell).join(','),
      ['History', response.history_basis].map(csvCell).join(','),
      ['Definitions version', response.definitions_version].map(csvCell).join(','),
      '',
      ...accountValidationCsvRows(summary),
      '',
      ['Metric', 'Registrations', 'Eligible', 'Converted', 'Pending', 'Rate %', 'Median hours', 'Mean hours'].map(csvCell).join(','),
      ['Registrations', summary.registrations, '', '', '', '', ''].map(csvCell).join(','),
      ...[1, 7, 14, 30].map((days) => {
        const metric = summary[`conversion_${days}d`] || {};
        return [
          `Converted within ${days}d`,
          summary.registrations,
          metric.eligible,
          metric.converted,
          metric.pending_maturity,
          metric.rate,
          metric.median_hours,
          metric.mean_hours,
        ].map(csvCell).join(',');
      }),
      '',
      ['Activation curve', 'Elapsed days', 'Eligible 30d', 'Converted', 'Rate %'].map(csvCell).join(','),
      ...(curve.rows || []).map((row) => [
        'Curve', row.elapsedDays, curve.eligible_30d, row.converted, row.rate,
      ].map(csvCell).join(',')),
      '',
      ['Cohort trend', 'Date from', 'Date to', 'Registrations', 'Eligible', 'Converted', 'Pending', 'Rate %', 'Complete'].map(csvCell).join(','),
      ...(response.trend || []).map((row) => [
        'Trend', row.dateFrom, row.dateTo, row.registrations, row.eligible,
        row.converted, row.pending_maturity, row.rate, row.complete,
      ].map(csvCell).join(',')),
      '',
      [`Breakdown: ${breakdownDimension}`, 'Segment', 'Registrations', 'Eligible', 'Converted', 'Pending', 'Rate %', 'Median hours'].map(csvCell).join(','),
      ...breakdown.map((row) => [
        breakdownDimension, row.label, row.registrations, row.eligible,
        row.converted, row.pending_maturity, row.rate, row.median_hours,
      ].map(csvCell).join(',')),
      '',
      ['Data quality', 'Count'].map(csvCell).join(','),
      ['Purchase before registration', dataQuality.purchase_before_registration].map(csvCell).join(','),
      ['Future first purchase', dataQuality.future_first_purchase].map(csvCell).join(','),
      ['Future registration', dataQuality.future_registration].map(csvCell).join(','),
      ['Excluded from activation', dataQuality.excluded_from_activation].map(csvCell).join(','),
      ['Future purchases ignored', dataQuality.future_purchases].map(csvCell).join(','),
      ...purchaseProgressionCsvRows(response).map((row) => row.map(csvCell).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `client_activation_${period.periodMode || 'period'}_${period.dateFrom || 'from'}_${period.dateTo || 'to'}_${county.value || 'all'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  render() {
    const {
      periodMode,
      countyFilter,
      windowDays,
      breakdownDimension,
      countyOptions,
      response,
      status,
      updatedAt,
    } = this.state;
    const summary = response?.summary || {};
    const selectedCounty = countyOptions.find(
      (option) => option.value === countyFilter,
    ) || { value: countyFilter, label: response?.filters?.county?.label || countyFilter };
    const loading = status === 'loading';
    const hasResponse = Boolean(response);
    const excluded = Number(response?.data_quality?.excluded_from_activation || 0);
    const showCohorts = this.props.reportView === 'cohorts';

    return (
      <Stack spacing={{ xs: 1, sm: 1.5 }} sx={{ minWidth: 0 }}>
        <Stack
          spacing={{ xs: 1, sm: 1.25 }}
          sx={reportHeaderSx}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={{ xs: 0.75, sm: 1.5 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={reportTitleSx}>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{showCohorts ? 'Registration cohorts' : 'Activation'}</Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{showCohorts ? 'Registration cohorts' : 'Activation & repeat purchases'}</Box>
              </Typography>
              <Typography sx={{ mt: 0.25, color: '#475569', fontSize: { xs: '0.72rem', sm: '0.82rem' } }}>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{showCohorts ? 'Compare client groups after the same number of days since registration' : 'First & repeat purchases'}</Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{showCohorts ? 'Compare client groups after the same number of days since registration' : 'Registration cohorts · activation = first purchase'}</Box>
              </Typography>
            </Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={periodMode}
              onChange={this.handlePeriodChange}
              aria-label="Activation registration cohort"
              sx={reportPeriodControlSx}
            >
              <ToggleButton value="last_30d" title="Registrations in the last 30 days">30d</ToggleButton>
              <ToggleButton value="last_90d" title="Registrations in the last 90 days">90d</ToggleButton>
              <ToggleButton value="last_180d" title="Registrations in the last 180 days">180d</ToggleButton>
              <ToggleButton
                ref={this.customPeriodButton}
                value="custom"
                title="Custom registration dates"
                aria-haspopup="dialog"
                aria-expanded={this.state.customPeriodOpen}
                aria-controls={this.state.customPeriodOpen ? 'activation-custom-period' : undefined}
                onClick={periodMode === 'custom' ? (event) => this.handlePeriodChange(event, 'custom') : undefined}
              >
                Custom
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <CustomPeriodPopover
            id="activation-custom-period"
            open={this.state.customPeriodOpen}
            anchorEl={this.customPeriodButton.current}
            onClose={() => this.setState({ customPeriodOpen: false })}
            dateFrom={this.state.draftDateFrom}
            dateTo={this.state.draftDateTo}
            onDateFromChange={(value) => this.handleCustomDateChange('dateFrom', value)}
            onDateToChange={(value) => this.handleCustomDateChange('dateTo', value)}
            onApply={this.handleCustomPeriodApply}
            error={this.state.customPeriodError}
            fromLabel="Registered from"
            toLabel="Registered through"
            description="Up to 366 registration days. Purchases are followed through the report cutoff."
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={{ xs: 0.65, sm: 1 }}
          >
            <Box sx={{ minWidth: 0, flex: 1, alignSelf: 'flex-start' }}>
              {hasResponse && (
                <Typography sx={{ color: '#64748b', fontSize: '0.74rem', fontWeight: 650 }}>
                  {formatPeriodRange(response.period)} · {selectedCounty.label}
                </Typography>
              )}
              {updatedAt && (
                <Typography sx={{ color: '#64748b', fontSize: '0.68rem', mt: 0.15, lineHeight: 1.35 }}>
                  Updated {formatUpdateTime(updatedAt)}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                onClick={() => this.loadActivation({ force: true })}
                disabled={loading}
                aria-label="Refresh"
                title="Refresh"
                sx={{ ...reportActionSx, '& .MuiButton-startIcon': { ml: 0, mr: { xs: 0, sm: 0.5 } } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
              </Button>
              {!showCohorts && <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadRoundedIcon />}
                onClick={this.exportCsv}
                disabled={!hasResponse || status !== 'success'}
                aria-label="Export activation CSV"
                title="Export activation CSV"
                sx={{ ...reportActionSx, '& .MuiButton-startIcon': { ml: 0, mr: { xs: 0, sm: 0.5 } } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>CSV</Box>
              </Button>}
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 1, sm: 1.5 }}
            sx={{ p: { xs: 1, sm: 1.15 }, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}
          >
            <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
              <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'rgba(26, 35, 126, 0.10)', color: '#1a237e', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <LocationOnRoundedIcon sx={{ fontSize: 19 }} />
              </Box>
              <Box minWidth={0}>
                <Typography sx={{ color: '#0f172a', fontWeight: 800, fontSize: '0.78rem' }}>
                  Client profile county
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.7rem', lineHeight: 1.25 }}>
                  Registrations by current address county.
                </Typography>
              </Box>
            </Stack>
            <Autocomplete
              size="small"
              disableClearable={countyFilter === 'all'}
              clearText="Reset to all counties"
              autoHighlight
              options={countyOptions}
              value={selectedCounty}
              onChange={this.handleCountyChange}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search or select county"
                  inputProps={{ ...params.inputProps, 'aria-label': 'Activation client profile county' }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnRoundedIcon sx={{ color: '#64748b', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              sx={{
                width: { xs: '100%', sm: 260, md: 290 },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  fontSize: { xs: '16px', sm: '0.8rem' },
                  fontWeight: 650,
                },
                '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                  fontSize: { xs: '16px', sm: '0.8rem' },
                },
              }}
            />
          </Stack>
        </Stack>

        {status === 'error' && (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" onClick={() => this.loadActivation({ force: true })}>
                Retry
              </Button>
            )}
          >
            {this.state.errorMessage || 'Activation data could not be loaded.'}
            {hasResponse && ' The previous result is still displayed with its original observation time.'}
          </Alert>
        )}

        {hasResponse && summary.registrations === 0 && <Alert severity="info">No client registrations match this period and county.</Alert>}
        {showCohorts ? (
          <ClientsCohorts response={response} status={status} />
        ) : (
          <>
            <ClientsActivationSummary summary={response?.summary} status={status} />
            <ClientsPurchaseProgression response={response} status={status} />
            <ClientsPurchaseBehavior response={response} status={status} />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={0.8}
              sx={{
                p: { xs: 1, sm: 1.2 },
                bgcolor: '#eff6ff',
                border: '1px solid #dbeafe',
                borderRadius: '12px',
              }}
            >
              <Typography sx={{ color: '#0f172a', fontSize: '0.8rem', fontWeight: 700 }}>Chart & breakdown window</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={windowDays}
                onChange={this.handleWindowChange}
                aria-label="Activation analysis window"
                sx={reportPeriodControlSx}
              >
                <ToggleButton value={1}>1d</ToggleButton>
                <ToggleButton value={7}>7d</ToggleButton>
                <ToggleButton value={14}>14d</ToggleButton>
                <ToggleButton value={30}>30d</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <ClientsActivationChart response={response} status={status} />

            <ClientReportDetails title="Purchase activation by client segment">
              <ClientsActivationBreakdown
                response={response}
                status={status}
                dimension={breakdownDimension}
                onDimensionChange={this.handleDimensionChange}
              />
            </ClientReportDetails>

            <ClientReportDetails title="Account validation & reminders" defaultExpanded>
              <ClientsAccountValidation
                validation={summary.account_validation}
                lifecycle={summary.account_lifecycle}
                status={status}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 0.75,
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={this.exportUnvalidatedCreditClients}
                  title="Export all currently unvalidated clients with credit"
                  sx={{
                    ...reportActionSx,
                    textTransform: 'none',
                  }}
                >
                  Export unvalidated with credit
                </Button>
              </Box>
            </ClientReportDetails>
          </>
        )}

        {excluded > 0 && (
          <Alert severity="warning">
            {numberFormatter.format(excluded)} client record{excluded === 1 ? '' : 's'} excluded because purchase dates precede registration or are in the future.
          </Alert>
        )}
      </Stack>
    );
  }
}

export default RapClientsActivation;
